<template>
  <div class="container-fluid">
    <!-- Accounts Table -->
    <div class="mt-2 row" id="element-to-print">
      <div
        class="container-fluild table-main remove-styles2 remove-border responsiveness"
      >
        <div v-if="bookType == 'pdf'" class="row d-none">
          <table
            class="table remove-styles mt-0 col-md-12 table-hover table-header-area"
          >
            <thead>
              <tr>
                <th>
                  <div class="row">
                    <div class="col-md-12 d-flex justify-content-between">
                      <div>
                        <div class="s-20 fw-500">Balance Sheet Report</div>
                        <div class="s-18 font-weight-600">
                          {{ tenantDisplayName }}
                        </div>
                        <div class="s-18 fw-500">
                          <span class="mr-1"> Date Range: </span>
                          <span>
                            {{ formatDate(startDate) }}
                            to {{ formatDate(endDate) }}
                          </span>
                        </div>
                      </div>
                      <div>
                        <img
                          :src="churchLogo"
                          v-if="churchLogo"
                          class="link-image mr-2"
                          alt=""
                        />
                        <img
                          src="../../../assets/dashboardlinks/churchcloud.png"
                          v-else
                          class="link-image mr-2"
                          alt=""
                        />
                      </div>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
          </table>
        </div>
        <!-- Main Report Table -->
        <table id="table" class="table remove-styles mt-0 table-hover table-header-area">
          <thead class="table-header-ar">
            <tr class="font-weight-bold">
              <th>ACCOUNTS</th>
              <th class="text-right"></th>
              <th class="text-right text-dak s-14">
                {{ formatDate(startDate) }} <br />
                to {{ formatDate(endDate) }}
              </th>
            </tr>
          </thead>

          <!-- Dynamic Sections -->
          <template
            v-for="(categoryData, categoryName) in balanceSheetData"
            :key="categoryName"
          >
            <!-- Only show sections that are arrays and have data -->
            <tbody v-if="Array.isArray(categoryData) && categoryData.length">
              <!-- Category Header (dynamically generated) -->
              <tr class="table-header-area-main">
                <td colspan="3" class="text-dak fw-400 s-14">
                  {{ formatCategoryName(categoryName) }}
                </td>
              </tr>

              <!-- Group accounts by accountGroup -->
              <template
                v-for="(group, groupName) in groupAccountsByType(categoryData)"
                :key="groupName"
              >
                <tr>
                  <td class="pl-3 text-blue-400 fw-500 s-14">Total {{ groupName }}</td>
                  <td></td>
                  <td class="text-right">
                    {{ currentUser.currencySymbol }}
                    {{ formatAmount(group.total) }}
                  </td>
                </tr>
              </template>

              <!-- Category Total -->
              <tr class="table-header-area-main">
                <td class="font-weight-bold">
                  Total {{ formatCategoryName(categoryName) }}
                </td>
                <td></td>
                <td class="text-right font-weight-bold">
                  {{ currentUser.currencySymbol }}
                  {{
                    formatAmount(
                      balanceSheetData[
                        `total${capitalizeFirstLetter(categoryName)}`
                      ] || calculateTotal(categoryData)
                    )
                  }}
                </td>
              </tr>
            </tbody>
          </template>

          <!-- Total Liabilities & Equity -->
          <!-- <tbody>
            <tr class="font-weight-bold">
              <td class="font-weight-bold">Total Liabilities & Equity</td>
              <td></td>
              <td class="text-right font-weight-bold">
                {{ currentUser.currencySymbol }}
                {{
                  formatAmount(
                    (balanceSheetData.totalLiabilities || 0) +
                      (balanceSheetData.totalEquity || 0)
                  )
                }}
              </td>
            </tr>
          </tbody> -->
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, watchEffect, ref, watch, nextTick } from "vue";
import { useStore } from "vuex";
import axios from "@/gateway/backendapi";
import exportService from "../../../services/exportFile/exportserjs";
import dateFormatter from "../../../services/dates/dateformatter";

const props = defineProps({
  startDate: {
    type: String,
    default: "",
  },
  endDate: {
    type: String,
    default: "",
  },
  fileHeaderToExport: {
    type: Array,
    required: true,
  },
  fileToExport: {
    type: Array,
    required: true,
  },
  fileName: {
    type: String,
    default: "",
  },
  bookType: {
    type: String,
    default: "",
  },
  balanceSheetDataSummaryData: {
    type: Object,
    default: () => ({}),
  },
});

const tenantData = ref({});
const churchLogo = ref("");
const store = useStore();

const balanceSheetData = computed(
  () => props.balanceSheetDataSummaryData || {}
);

// Group accounts by their accountGroup

const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

// Helper function to format category names
const formatCategoryName = (name) => {
  return name
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

// Group accounts by their accountGroup


const groupAccountsByType = (accounts) => {
  const groups = {};
  accounts.forEach((account) => {
    const groupName = account.accountGroup || "Other";
    if (!groups[groupName]) {
      groups[groupName] = {
        total: 0,
      };
    }
    groups[groupName].total += account.totalAmount || 0;
  });
  return groups;
};

// Calculate total for a category if not provided by backend
const calculateTotal = (category) => {
  const items = balanceSheetData.value[category] || [];
  return items.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
};

// Sample Data

const emit = defineEmits(["download-complete"]); // Add this line

// Watch for changes in bookType prop
watch(
  () => props.bookType,
  async (newVal, oldVal) => {
    if (!newVal || newVal === oldVal) return;

    try {
      if (newVal === "pdf") await nextTick();

      exportService.downLoadExcel(
        newVal,
        document.getElementById("element-to-print"),
        props.fileName,
        props.fileHeaderToExport,
        props.fileToExport
      );

      emit("download-complete", true);
    } catch (error) {
      emit("download-complete", false);
    }
  },
  { immediate: true } // Changed to true to catch all changes
);

const getChurchProfile = async () => {
  try {
    let { data } = await axios.get(
      `/GetChurchProfileById?tenantId=${getUser.value.tenantId}`
    );
    console.log(data);
    churchLogo.value = data.returnObject.logo;
  } catch (err) {
    console.error(err);
  }
};

const tenantDisplayName = computed(() => {
  if (!tenantData.value.churchName) return "";
  const name =
    tenantData.value.churchName.length < 20
      ? tenantData.value.churchName
      : `${tenantData.value.churchName.slice(0, 20)}...`;
  return name;
});
const getUser = computed(() => {
  if (
    !store.getters.currentUser ||
    (store.getters.currentUser &&
      Object.keys(store.getters.currentUser).length == 0)
  )
    return "";
  return store.getters.currentUser;
});

watchEffect(() => {
  if (getUser.value) {
    tenantData.value = getUser.value;
    getChurchProfile();
  }
});
const currentUser = computed(() => {
  if (store && Object.keys(store.getters.currentUser).length > 0)
    return store.getters.currentUser;
  return "";
});

const formatDate = (date) => {
  return dateFormatter.monthDayYear(date);
};

// Format Amount
const formatAmount = (amount) =>
  amount.toLocaleString("en-NG", { minimumFractionDigits: 2 });
</script>

<style scoped>
.table th,
.table td {
  border-top: none;
}
.active {
  background: #ffffff;
  color: #08162e;
  font-weight: 700;
  width: 81px;
  height: 37px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  /* padding: 15px 0; */
}
.bg-secondary {
  background-color: #f8f9fa !important;
}
button.el-button {
  height: 36px !important;
  width: 139px !important;
  padding: 13px 24px !important;
  font-size: 13px !important;
}
.table-main {
  width: 100% !important;
  box-shadow: 0 0.063rem 0.25rem #02172e45 !important;
  /* border: 0.063rem solid #dde2e6 !important; */
  /* border-radius: 30px !important; */
  text-align: left !important;
  margin-bottom: auto !important;
  padding-bottom: 0.5rem !important;
}

.remove-styles {
  border: none !important;
  box-shadow: none !important;
  border-bottom: 0 !important;
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}
.link-image {
  width: 44.88px;
  height: 43.52px;
  padding-right: 0;
  border-radius: 8px;
  object-fit: cover;
}

.remove-styles2 {
  padding-right: 0;
  padding-left: 0;
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
}
table tbody tr,
table thead tr {
  border-bottom: 1px solid rgba(0, 0, 1, 0.2);
}
table tbody tr:first-child {
  border-bottom: none !important;
}
table tbody tr:last-child {
  border-bottom: none !important;
}

.remove-border {
  box-shadow: none !important;
}
.table {
  width: 100% !important;
  box-shadow: none !important;
  border: none !important;
  text-align: left;
  margin-bottom: auto !important;
  padding-bottom: 0.5rem;
}

.table thead th {
  font-weight: 700 !important;
  color: #000000 !important;
  font-size: 14px !important;
}
.table tbody tr {
  font-weight: 400 !important;
  color: #000000 !important;
  font-size: 14px !important;
}

.table-header-area-main {
  background-color: #d4dde4;
  /* background-color: #f4f4f4; */
}

.table-header-area {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
.pdf-only {
  display: none;
}

/* Print/PDF specific CSS */
@media print {
  .pdf-only {
    display: block;
  }

  .no-print {
    display: none;
  }
}
</style>
